import './NavigationLayout.less';

import { useContext, useEffect, useState, type ReactNode } from 'react';
import {
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  DownOutlined,
} from '@ant-design/icons';
import {
  Menu,
  Layout,
  Button,
  Tooltip,
  Dropdown,
  type MenuProps,
} from 'antd';
import { motion } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import cookie from 'js-cookie';
import IncortaLogo from 'images/incorta-logo.svg';
import { SessionContext } from 'auth/SessionProvider';
import { CliDockSpacer, setCliDockInset } from 'components/Cli/CliDock';
import { PageSkeleton } from 'components/PageSkeleton/PageSkeleton';
import { useAccess } from 'hooks/useAccess';
import { canSeeHome } from 'components/Home/homeWidgets';
import { recordVisit } from 'navigation/recentPages';
import {
  ADMIN_PAGES,
  AdminPage,
  NAV_GROUPS,
  NavGroupKey,
  canOpen,
  navPathOf,
  skeletonAt,
} from 'navigation/adminPages';

const { Content } = Layout;

const LOGO_WIDTH = 138;
const LOGO_COLLAPSED_WIDTH = 56;
const SIDER_WIDTH = 240;
const SIDER_COLLAPSED_WIDTH = 80;
const SIDER_TRANSITION = {
  type: 'spring',
  visualDuration: 0.18,
  bounce: 0,
} as const;

function withSubMenuTooltip(
  icon: ReactNode,
  label: string,
  collapsed: boolean,
) {
  if (!collapsed) return icon;

  return (
    <Tooltip
      title={label}
      placement="topRight"
      overlayClassName="ant-menu-inline-collapsed-tooltip"
      mouseEnterDelay={0.7}
    >
      <span className="NavigationLayout__submenu-icon-trigger">{icon}</span>
    </Tooltip>
  );
}

// A header bar holds this many top-level entries; an admin with more keeps the sidebar.
const HEADER_MAX_ENTRIES = 5;

type NavGroupEntry = { group: NavGroupKey; pages: AdminPage[] };
type NavEntry = { page: AdminPage } | NavGroupEntry;

const isGroup = (entry: NavEntry): entry is NavGroupEntry => 'group' in entry;

/** Pages in listed order, grouped; a group of one page stands as that page. */
function navEntries(pages: AdminPage[]): NavEntry[] {
  const entries: NavEntry[] = [];
  for (const page of pages) {
    const group = entries
      .filter(isGroup)
      .find(entry => entry.group === page.group);
    if (!page.group) entries.push({ page });
    else if (group) group.pages.push(page);
    else entries.push({ group: page.group, pages: [page] });
  }
  return entries.map(entry =>
    isGroup(entry) && entry.pages.length === 1
      ? { page: entry.pages[0] }
      : entry,
  );
}

/** The navigation item the location belongs to: the longest path it starts with. */
function selectedNavPath(pathname: string, navPaths: string[]) {
  const [longest] = navPaths
    .filter(path => pathname === path || pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length);
  return longest ?? pathname;
}

// Outlives the layout: a full-screen page (a cluster's infrastructure map) leaves the frame, and coming
// back finds the sidebar as it was left.
let siderCollapsed = true;

function NavigationLayout({ children }) {
  const location = useLocation();
  const rawHistory = useNavigate();
  const { user, logout } = useContext(SessionContext);
  const { can, canAny } = useAccess();

  const [collapsed, setCollapsed] = useState(siderCollapsed);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isSiderAnimating, setIsSiderAnimating] = useState(false);

  const listedPages = ADMIN_PAGES.filter(
    page => page.label && canOpen(page, canAny),
  );
  const entries = navEntries(listedPages.filter(page => !page.account));
  const showsHome = canSeeHome(can);
  const isHeader = entries.length + (showsHome ? 1 : 0) <= HEADER_MAX_ENTRIES;

  useEffect(() => {
    setIsNavigating(false);
    recordVisit(location.pathname);
  }, [location.pathname]);

  // The dock is fixed to the window, so it is told where the page starts rather than measuring it.
  useEffect(() => {
    const siderWidth = collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;
    setCliDockInset(isHeader ? 0 : siderWidth);
  }, [collapsed, isHeader]);

  // Pages outside the frame, like the full-screen map, have no sider for the dock to clear.
  useEffect(() => () => setCliDockInset(0), []);

  function history(path: string) {
    if (path !== location.pathname) setIsNavigating(true);
    rawHistory(path);
  }

  const selectedKey = selectedNavPath(
    location.pathname,
    listedPages.map(navPathOf),
  );
  const activeParentKey =
    entries
      .filter(isGroup)
      .find(entry => entry.pages.some(page => navPathOf(page) === selectedKey))
      ?.group ?? null;

  function handleToggleCollapsed() {
    if (collapsed) setIsSiderAnimating(true);
    siderCollapsed = !collapsed;
    setCollapsed(!collapsed);
    setOpenKeys([]);
  }

  // Hold the submenu closed until the sider has finished widening, so the two never reflow together
  useEffect(() => {
    if (collapsed || isSiderAnimating) return;
    setOpenKeys(activeParentKey ? [activeParentKey] : []);
  }, [activeParentKey, collapsed, isSiderAnimating]);

  function handleLogout() {
    logout({ returnTo: window.location.origin });
    cookie.remove('CloudAdminSSO', {
      domain: process.env.REACT_APP_DOMAIN,
    });
    document.dispatchEvent(new CustomEvent('SESSION_LOGGED_OUT'));
  }

  const pageItem = (page: AdminPage) => ({
    key: navPathOf(page),
    icon: page.icon,
    label: page.label,
    className: page.className,
    onClick: () => history(navPathOf(page)),
  });

  const accountItems: MenuProps['items'] = [
    ...listedPages.filter(page => page.account).map(pageItem),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log out',
      onClick: handleLogout,
    },
  ];

  const menuItems: MenuProps['items'] = [
    ...(showsHome
      ? [
          {
            key: '/',
            icon: <HomeOutlined />,
            label: 'Home',
            onClick: () => history('/'),
          },
        ]
      : []),
    ...entries.map(entry => {
      if (!isGroup(entry)) return pageItem(entry.page);
      const { label, icon } = NAV_GROUPS[entry.group];
      return {
        key: entry.group,
        label,
        icon: isHeader ? icon : withSubMenuTooltip(icon, label, collapsed),
        children: entry.pages.map(pageItem),
      };
    }),
  ];

  const content = (
    <Content className="NavigationLayout__content">
      {isNavigating ? (
        <PageSkeleton kind={skeletonAt(location.pathname)} />
      ) : (
        children
      )}
      <CliDockSpacer />
    </Content>
  );

  if (isHeader) {
    return (
      <Layout className="NavigationLayout NavigationLayout--header">
        <Layout.Header className="NavigationLayout__header">
          <img
            src={IncortaLogo}
            alt="Incorta Logo"
            className="NavigationLayout__header-logo"
          />
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[selectedKey]}
            className="NavigationLayout__header-menu"
            items={menuItems}
          />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{ items: accountItems, selectedKeys: [selectedKey] }}
          >
            <Button
              type="text"
              icon={<UserOutlined />}
              className="NavigationLayout__user"
            >
              {user.fullName || user.email}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Layout.Header>
        {content}
      </Layout>
    );
  }

  return (
    <Layout hasSider className="NavigationLayout">
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH }}
        transition={SIDER_TRANSITION}
        onAnimationComplete={() => setIsSiderAnimating(false)}
        className={`NavigationLayout__sider${
          isSiderAnimating || collapsed
            ? ' NavigationLayout__sider--toggling'
            : ''
        }`}
      >
        <div className="NavigationLayout__sider-inner">
          <div className="NavigationLayout__logo">
            <motion.img
              src={IncortaLogo}
              alt="Incorta Logo"
              initial={false}
              animate={{
                width: collapsed ? LOGO_COLLAPSED_WIDTH : LOGO_WIDTH,
              }}
              transition={SIDER_TRANSITION}
            />
          </div>
          <Menu
            theme="dark"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[selectedKey]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            triggerSubMenuAction="hover"
            getPopupContainer={() => document.body}
            className="NavigationLayout__menu"
            items={menuItems}
          />
          <Menu
            theme="dark"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={[selectedKey]}
            className="NavigationLayout__footer-menu"
            items={accountItems}
          />
          <Button
            type="text"
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={handleToggleCollapsed}
            className="NavigationLayout__sider-toggle"
          />
        </div>
      </motion.aside>
      <Layout className="NavigationLayout__site-layout">{content}</Layout>
    </Layout>
  );
}

export default NavigationLayout;
